import { createReducer, on } from '@ngrx/store';

import { Salutation } from '@vi-iot/client';

import { GatewayCommissioningActions } from './gateway-commissioning.actions';
import {
  GATEWAY_COMMISSIONING_DEVICE,
  GATEWAY_COMMISSIONING_FLOW,
  GATEWAY_COMMISSIONING_STEP,
} from './gateway-commissioning.model';
import { GatewayCommissioningState } from './gateway-commissioning.state';

export const initGatewayCommissioningState: GatewayCommissioningState = {
  activeStep: GATEWAY_COMMISSIONING_STEP.SELECT_INSTALLATION_TYPE,
  selectedDevice: GATEWAY_COMMISSIONING_DEVICE.NONE,
  commissioningFlow: GATEWAY_COMMISSIONING_FLOW.RESIDENTIAL,
  gateway: {
    serial: '',
    password: '',
  },
  description: '',
  address: {
    street: '',
    houseNumber: '',
    zip: '',
    region: '',
    city: '',
    country: '',
  },
  email: '',
  heatingType: undefined,
  installationId: undefined,
  transferInstallationLoading: false,
  transferInstallationSuccess: false,
  transferOMAInstallationSuccess: false,
  registerGatewayError: false,
  sendInvitationError: false,
  gatewayVerificationData: undefined,
  gatewayVerificationLoading: false,
  gatewayVerificationError: null,
  withoutOwner: false,
  installationOwnerData: {
    firstName: '',
    lastName: '',
    salutation: Salutation.Mr,
    privacyPolicyAccepted: false,
    privacyPolicyAcceptanceUtc: '',
  },
  isGrandmaFunctionSelected: false,
  isOwnerOfTheInstallationSelected: true,
};

export const gatewayCommissioningReducer = createReducer(
  initGatewayCommissioningState,

  on(
    GatewayCommissioningActions.setActiveStep,
    (state, { activeStep }): GatewayCommissioningState => ({
      ...state,
      activeStep,
      transferInstallationLoading: false,
      transferInstallationSuccess: false,
      registerGatewayError: false,
      sendInvitationError: false,
    })
  ),

  on(
    GatewayCommissioningActions.setFormData,
    (state, { formData }): GatewayCommissioningState => ({
      ...state,
      address: formData?.address ? formData.address : state.address,
      email: formData?.email ? formData.email : state.email,
      gateway: formData?.gateway ? formData.gateway : state.gateway,
      heatingType: formData?.heatingType ? formData.heatingType : state.heatingType,
      description: formData?.description !== undefined ? formData.description : state.description,
      installationOwnerData: formData?.installationOwnerData
        ? formData.installationOwnerData
        : state.installationOwnerData,
      isGrandmaFunctionSelected:
        formData?.isGrandmaFunctionSelected !== undefined
          ? formData.isGrandmaFunctionSelected
          : state.isGrandmaFunctionSelected,
      isOwnerOfTheInstallationSelected:
        formData?.isOwnerOfTheInstallationSelected !== undefined
          ? formData.isOwnerOfTheInstallationSelected
          : state.isOwnerOfTheInstallationSelected,
    })
  ),

  on(
    GatewayCommissioningActions.setSelectedDevice,
    (state, { selectedDevice }): GatewayCommissioningState => ({
      ...state,
      selectedDevice,
    })
  ),

  on(
    GatewayCommissioningActions.setCommissioningFlow,
    (state, { commissioningFlow }): GatewayCommissioningState => ({
      ...state,
      commissioningFlow,
    })
  ),

  on(
    GatewayCommissioningActions.registerGateway.attempt,
    (state): GatewayCommissioningState => ({
      ...state,
      transferInstallationLoading: true,
      transferInstallationSuccess: false,
      registerGatewayError: false,
      sendInvitationError: false,
    })
  ),

  on(
    GatewayCommissioningActions.registerGateway.success,
    (state, { withoutOwner }): GatewayCommissioningState => ({
      ...state,
      activeStep: withoutOwner
        ? GATEWAY_COMMISSIONING_STEP.TRANSFER_OMA_INSTALLATION_SUCCESS
        : GATEWAY_COMMISSIONING_STEP.TRANSFER_INSTALLATION_SUCCESS,
      transferInstallationLoading: false,
      transferInstallationSuccess: false,
      transferOMAInstallationSuccess: true,
      registerGatewayError: false,
      sendInvitationError: false,
    })
  ),

  on(
    GatewayCommissioningActions.registerGateway.failed,
    (state): GatewayCommissioningState => ({
      ...state,
      transferInstallationLoading: false,
      transferInstallationSuccess: false,
      registerGatewayError: true,
      sendInvitationError: false,
    })
  ),

  on(
    GatewayCommissioningActions.commercialRegisterGateway.attempt,
    (state): GatewayCommissioningState => ({
      ...state,
      transferInstallationLoading: true,
      transferInstallationSuccess: false,
      registerGatewayError: false,
    })
  ),

  on(
    GatewayCommissioningActions.commercialRegisterGateway.success,
    (state): GatewayCommissioningState => ({
      ...state,
      activeStep: GATEWAY_COMMISSIONING_STEP.TRANSFER_COMMERCIAL_INSTALLATION_SUCCESS,
      transferInstallationLoading: false,
      transferInstallationSuccess: false,
      registerGatewayError: false,
    })
  ),

  on(
    GatewayCommissioningActions.commercialRegisterGateway.failed,
    (state): GatewayCommissioningState => ({
      ...state,
      transferInstallationLoading: false,
      transferInstallationSuccess: false,
      registerGatewayError: true,
    })
  ),

  on(
    GatewayCommissioningActions.sendInvitation.attempt,
    (state, { installationId }): GatewayCommissioningState => ({
      ...state,
      installationId,
      transferInstallationLoading: true,
      transferInstallationSuccess: false,
      registerGatewayError: false,
      sendInvitationError: false,
    })
  ),

  on(
    GatewayCommissioningActions.sendInvitation.success,
    (state): GatewayCommissioningState => ({
      ...state,
      activeStep: GATEWAY_COMMISSIONING_STEP.TRANSFER_INSTALLATION_SUCCESS,
      transferInstallationLoading: false,
      transferInstallationSuccess: true,
      registerGatewayError: false,
      sendInvitationError: false,
    })
  ),

  on(
    GatewayCommissioningActions.sendInvitation.failed,
    (state): GatewayCommissioningState => ({
      ...state,
      transferInstallationLoading: false,
      transferInstallationSuccess: false,
      registerGatewayError: false,
      sendInvitationError: true,
    })
  ),

  on(
    GatewayCommissioningActions.verifyGateway.attempt,
    (state): GatewayCommissioningState => ({
      ...state,
      gatewayVerificationLoading: true,
      gatewayVerificationData: null,
      gatewayVerificationError: null,
    })
  ),

  on(
    GatewayCommissioningActions.verifyGateway.failed,
    (state, { error }): GatewayCommissioningState => ({
      ...state,
      gatewayVerificationLoading: false,
      gatewayVerificationData: null,
      gatewayVerificationError: error,
    })
  ),

  on(
    GatewayCommissioningActions.verifyGateway.success,
    (state, { verifyGateway }): GatewayCommissioningState => ({
      ...state,
      gatewayVerificationLoading: false,
      gatewayVerificationData: verifyGateway,
      gatewayVerificationError: null,
    })
  ),

  on(
    GatewayCommissioningActions.clearState,
    (): GatewayCommissioningState => ({
      ...initGatewayCommissioningState,
    })
  )
);
